import React, { useEffect, useRef, useState } from "react";
import { api, coverUrl } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, X, Music, Loader2 } from "lucide-react";

export const SECTION_OPTIONS = [
  { key: "remix", label: "Remixes" },
  { key: "mashup", label: "Mashups" },
  { key: "pool", label: "Pool" },
  { key: "original", label: "Originales" },
  { key: "highlighted", label: "Destacados" },
  { key: "new", label: "Novedades" },
  { key: "top", label: "Lo más top" },
  { key: "home", label: "Home" },
];

export function SectionsAdmin({ onChange }) {
  const [key, setKey] = useState("remix");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [data, setData] = useState({ members: [], candidates: [], pistas: [] });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const timer = useRef(null);
  const reqId = useRef(0);

  const load = async (k = key, query = q) => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const r = await api.get(`/admin/sections/${k}`, { params: { q: query } });
      if (id !== reqId.current) return;
      setData(r.data);
    } catch { if (id === reqId.current) toast.error("No se pudo cargar la sección"); }
    finally { if (id === reqId.current) setLoading(false); }
  };

  useEffect(() => { setQ(""); setFilter(""); setData({ members: [], candidates: [], pistas: [] }); load(key, ""); /* eslint-disable-next-line */ }, [key]);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(key, q), 350);
    return () => clearTimeout(timer.current);
    /* eslint-disable-next-line */
  }, [q]);

  const mutate = async (action, item) => {
    const id = item.kind === "pista" ? item.pista_id : item.id;
    setBusy(id);
    try {
      const body = item.kind === "pista"
        ? { action, pistas: [{ pack_id: item.pack_id, pista_id: item.pista_id }] }
        : { action, track_ids: [item.id] };
      await api.post(`/admin/sections/${key}/members`, body);
      toast.success(action === "add" ? "Añadido a la sección" : "Quitado de la sección");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(null); }
  };

  const label = SECTION_OPTIONS.find((s) => s.key === key)?.label;
  const members = data.members.filter((t) => !filter || `${t.title} ${t.artist} ${t.genre || ""}`.toLowerCase().includes(filter.toLowerCase()));
  const found = [...data.candidates, ...data.pistas];

  return (
    <div className="space-y-5" data-testid="sections-admin">
      <div className="flex flex-wrap gap-2" data-testid="sections-selector">
        {SECTION_OPTIONS.map((s) => (
          <button key={s.key} onClick={() => setKey(s.key)} data-testid={`sections-pick-${s.key}`}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${key === s.key ? "bg-red-600 text-white" : "bg-white/5 text-white/50 hover:text-white hover:bg-white/10"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Panel title={`En ${label}`} count={loading ? "…" : data.members.length} testid="sections-members-panel">
          <SearchBox value={filter} onChange={setFilter} placeholder="Filtrar los que ya están…" testid="sections-filter-input" />
          <Rows items={members} empty="Esta sección está vacía." testid="sections-member" busy={busy} loading={loading}
            action={(item) => (
              <button onClick={() => mutate("remove", item)} disabled={busy === item.id} data-testid={`sections-remove-${item.id}`} title="Quitar de la sección"
                className="shrink-0 w-8 h-8 rounded-full border border-white/10 text-white/50 hover:text-white hover:border-red-600 hover:bg-red-600/20 flex items-center justify-center disabled:opacity-40">
                {busy === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
              </button>
            )} />
        </Panel>

        <Panel title="Añadir temas" count={loading ? "…" : found.length} testid="sections-search-panel">
          <SearchBox value={q} onChange={setQ} placeholder="Buscar por título, artista, género o tag…" testid="sections-search-input" autoFocus />
          <Rows items={found} empty={q ? "Sin resultados fuera de esta sección." : "Escribe para buscar. Se muestran los más recientes."} testid="sections-candidate" busy={busy} loading={loading}
            action={(item) => {
              const id = item.kind === "pista" ? item.pista_id : item.id;
              return (
                <button onClick={() => mutate("add", item)} disabled={busy === id} data-testid={`sections-add-${id}`} title="Añadir a la sección"
                  className="shrink-0 h-8 px-3 rounded-full bg-red-600 hover:bg-red-500 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-40">
                  {busy === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Añadir
                </button>
              );
            }} />
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, count, children, testid }) {
  return (
    <div className="bg-[#111116] border border-white/5 rounded-2xl p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">{title}</h3>
        <span className="font-mono text-[11px] text-white/40 uppercase tracking-widest" data-testid={`${testid}-count`}>{count} temas</span>
      </div>
      {children}
    </div>
  );
}

function SearchBox({ value, onChange, placeholder, testid, autoFocus }) {
  return (
    <div className="relative">
      <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} data-testid={testid}
        className="w-full bg-[#16161D] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white text-sm placeholder:text-white/30 focus:border-red-600/60 outline-none" />
    </div>
  );
}

function Rows({ items, empty, testid, action, loading }) {
  if (loading) return <div className="py-10 flex justify-center text-white/40"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!items.length) return <p className="text-sm text-white/40 py-8 text-center" data-testid={`${testid}-empty`}>{empty}</p>;
  return (
    <div className="max-h-[60vh] overflow-y-auto space-y-1 pr-1" data-testid={`${testid}-list`}>
      {items.map((t) => {
        const id = t.kind === "pista" ? t.pista_id : t.id;
        const cover = coverUrl(t.cover_path, 160);
        return (
          <div key={id} className="flex items-center gap-3 bg-black/30 hover:bg-black/50 rounded-lg px-2 py-1.5" data-testid={`${testid}-row-${id}`}>
            {cover ? <img src={cover} alt="" className="w-9 h-9 rounded object-cover shrink-0" /> : <div className="w-9 h-9 rounded bg-white/5 flex items-center justify-center shrink-0"><Music className="w-4 h-4 text-white/30" /></div>}
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{t.title}</div>
              <div className="text-[11px] text-white/40 truncate flex gap-1.5 flex-wrap">
                <span>{t.artist}</span>
                {t.genre && <span className="text-white/25">· {t.genre}</span>}
                {t.kind === "pista" && <span className="text-sky-400">· Pista de «{t.pack_title}» (sin publicar)</span>}
                {t.kind === "track" && t.pack_id && <span className="text-sky-400">· Pack</span>}
                {t.kind === "track" && t.category && <span className="text-white/25">· {SECTION_OPTIONS.find((s) => s.key === t.category)?.label || t.category}</span>}
              </div>
            </div>
            {action(t)}
          </div>
        );
      })}
    </div>
  );
}
